import { getTaskTypeDescriptor } from '../../taskTypes/descriptor';
import { CampaignTaskType } from '../../types';
import { isTaskTypeClaimingPaused } from '../../domain/taskPolicy';
import { claimScopeKey } from '../../domain/taskQueue/claimScope';
import type { TaskClaimScope } from '../../domain/taskQueue/claimScope';

export type { TaskClaimScope };

/**
 * One claimable pool in the queue: a task type, optionally narrowed to a
 * domain. Defined here rather than in `types.ts` so the helpers that build
 * options do not import the props module — which imports the viewmodel,
 * which imports these helpers.
 */
export interface ClaimOption {
  taskTypeId: string | null;
  domainId: string | null;
  shortName: string | null;
  label: string;
  availableCount: number;
  claimingPaused?: boolean;
  maxReached?: boolean;
}


export function claimOptionKey(option: Pick<ClaimOption, 'taskTypeId' | 'domainId'>): string {
  return option.taskTypeId ? claimScopeKey(option.taskTypeId, option.domainId) : '';
}

export function campaignClaimScopes(taskTypes: CampaignTaskType[]): TaskClaimScope[] {
  return taskTypes.flatMap((taskType): TaskClaimScope[] => {
    if (taskType.domains && taskType.domains.length > 0) {
      return taskType.domains.map((domain) => ({
        taskTypeId: taskType.taskTypeId,
        domainId: domain.domainId,
      }));
    }
    return [{ taskTypeId: taskType.taskTypeId, domainId: null }];
  });
}

/**
 * Build the options list the claim button uses: one entry per task type bound
 * to the campaign, or a single untyped entry for legacy campaigns.
 *
 * Counts are passed in as a `Map<taskTypeId, number>` instead of derived from
 * a task list so the queue page can feed exact server-side count() results
 * for "available" (which can exceed the streaming cap on big campaigns)
 * alongside live stream counts for "awaiting review" (small in practice).
 * The empty key (`""`) is the catch-all for untyped campaigns.
 */
export function buildClaimOptions(
  taskTypes: CampaignTaskType[],
  countsByType: Map<string, number>,
  options: { respectClaimingPaused?: boolean; maxReached?: boolean; claimMaxReached?: boolean } = {},
): ClaimOption[] {
  const respectClaimingPaused = options.respectClaimingPaused !== false;
  const maxReached = options.maxReached === true || options.claimMaxReached === true;
  if (taskTypes.length === 0) {
    return [
      {
        taskTypeId: null,
        domainId: null,
        shortName: null,
        label: 'Any task',
        availableCount: countsByType.get('') ?? 0,
        maxReached,
      },
    ];
  }

  return taskTypes.flatMap((taskType): ClaimOption[] => {
    const mod = getTaskTypeDescriptor(taskType.taskTypeId);
    if (taskType.domains && taskType.domains.length > 0) {
      return taskType.domains.map((domain) => ({
        taskTypeId: taskType.taskTypeId,
        domainId: domain.domainId,
        shortName: mod.shortName,
        label: `${domain.label} ${mod.shortName}`,
        availableCount: countsByType.get(claimScopeKey(taskType.taskTypeId, domain.domainId)) ?? 0,
        claimingPaused: respectClaimingPaused && isTaskTypeClaimingPaused(taskType, domain.domainId),
        maxReached,
      }));
    }
    return [{
      taskTypeId: taskType.taskTypeId,
      domainId: null,
      shortName: mod.shortName,
      label: mod.shortName,
      availableCount: countsByType.get(taskType.taskTypeId) ?? 0,
      claimingPaused: respectClaimingPaused && isTaskTypeClaimingPaused(taskType),
      maxReached,
    }];
  });
}
